import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Send } from "lucide-react";

export default function TakeTest() {
  const { testId } = useParams();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const storeKey = `ct_answers_${testId}`;

  useEffect(() => {
    api.get(`/student/test/${testId}`).then((r) => setData(r.data)).catch((e) => setError(errText(e)));
    const saved = localStorage.getItem(storeKey);
    if (saved) setAnswers(JSON.parse(saved));
    // eslint-disable-next-line
  }, [testId]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const pick = (qid, i) => {
    const next = { ...answers, [qid]: i };
    setAnswers(next);
    localStorage.setItem(storeKey, JSON.stringify(next));
  };

  const submit = async () => {
    const unanswered = data.questions.filter((q) => answers[q.id] === undefined).length;
    if (unanswered && !window.confirm(`${unanswered} question(s) unanswered. Submit anyway?`)) return;
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/student/test/${testId}/submit`, { answers });
      localStorage.removeItem(storeKey);
      navigate(`/result/${res.id}`, { replace: true });
    } catch (e) { toast.error(errText(e)); } finally { setSubmitting(false); }
  };

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-slate-700" data-testid="test-error">{error}</p>
      <Button onClick={() => navigate("/student")} data-testid="back-dashboard-btn">Back to Dashboard</Button>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading test…</div>;

  const q = data.questions[idx];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h4 className="font-semibold truncate" data-testid="test-title">{data.test.title}</h4>
              <p className="text-xs text-slate-500">{data.chapter.name}</p>
            </div>
            <span className="text-sm text-slate-600 shrink-0" data-testid="answered-count">{answeredCount}/{data.questions.length} answered</span>
          </div>
          <Progress value={(answeredCount / data.questions.length) * 100} className="h-1.5 mt-3" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-sm text-slate-500">Question {idx + 1} of {data.questions.length}</p>
        <h2 className="text-2xl sm:text-3xl font-head font-bold mt-3 leading-snug" data-testid="question-text">{q.text}</h2>

        <div className="space-y-3 mt-8" data-testid="options-list">
          {q.options.map((o, i) => (
            <button key={i} onClick={() => pick(q.id, i)} data-testid={`option-${i}`}
              className={`w-full text-left px-5 py-4 rounded-2xl border transition-colors ${answers[q.id] === i ? "border-primary bg-accent" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
              <span className="font-medium mr-3 text-primary">{String.fromCharCode(65 + i)}</span>{o}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-10">
          <Button variant="outline" className="rounded-full" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="prev-question-btn">
            <ChevronLeft className="h-4 w-4 mr-1" />Previous
          </Button>
          {idx < data.questions.length - 1 ? (
            <Button className="rounded-full" onClick={() => setIdx(idx + 1)} data-testid="next-question-btn">
              Next<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button className="rounded-full" onClick={submit} disabled={submitting} data-testid="submit-test-btn">
              <Send className="h-4 w-4 mr-1" />{submitting ? "Submitting…" : "Submit Test"}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mt-10" data-testid="question-nav">
          {data.questions.map((qq, i) => (
            <button key={qq.id} onClick={() => setIdx(i)} data-testid={`qnav-${i}`}
              className={`h-9 w-9 rounded-lg text-sm border transition-colors ${i === idx ? "border-primary bg-primary text-white" : answers[qq.id] !== undefined ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white"}`}>
              {i + 1}
            </button>
          ))}
        </div>
        {idx < data.questions.length - 1 && (
          <Button variant="outline" className="rounded-full mt-8 w-full" onClick={submit} disabled={submitting} data-testid="submit-test-btn-bottom">
            <Send className="h-4 w-4 mr-1" />Submit Test
          </Button>
        )}
      </main>
    </div>
  );
}
